import React from 'react';
import LoopXSvg from '../../assets/LoopX.svg';
import LoopXLargeSvg from '../../assets/LoopX-large.svg';
import { useTheme } from '../../contexts/ThemeContext';

interface LogoProps {
    size?: 'sm' | 'md' | 'lg' | 'xl' | number;
    variant?: 'primary' | 'monochrome' | 'icon-only' | 'svg' | 'svg-large';
    className?: string;
    animated?: boolean;
    darkMode?: boolean;
    loading?: boolean;
}

export const Logo: React.FC<LogoProps> = ({
    size = 'md',
    variant = 'primary',
    className = '',
    animated = false,
    darkMode,
    loading = false
}) => {
    const { actualTheme } = useTheme();
    const logoId = `logo-${Math.random().toString(36).substring(2, 9)}`;

    // Responsive sizing system (24px to 48px)
    const getSizeValue = (size: 'sm' | 'md' | 'lg' | 'xl' | number): number => {
        if (typeof size === 'number') return size;

        const sizeMap = {
            sm: 24,   // Small - mobile/compact
            md: 32,   // Medium - default
            lg: 40,   // Large - desktop
            xl: 48    // Extra large - hero sections
        };

        return sizeMap[size];
    };

    const sizeValue = getSizeValue(size);

    // Use theme context or explicit darkMode prop
    const isDarkMode = darkMode ?? (actualTheme === 'dark');

    const renderGradients = () => (
        <defs>
            <linearGradient id={`${logoId}-primary`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={isDarkMode ? "#60A5FA" : "#3B82F6"} />
                <stop offset="100%" stopColor={isDarkMode ? "#A78BFA" : "#8B5CF6"} />
            </linearGradient>
            {loading && (
                <animateTransform
                    attributeName="gradientTransform"
                    attributeType="XML"
                    type="rotate"
                    from="0 12 12"
                    to="360 12 12"
                    dur="2s"
                    repeatCount="indefinite"
                />
            )}
        </defs>
    );

    const getFillColor = () => {
        switch (variant) {
            case 'monochrome':
                return 'currentColor';
            case 'icon-only':
                return isDarkMode ? '#A78BFA' : '#8B5CF6';
            case 'primary':
            default:
                return `url(#${logoId}-primary)`;
        }
    };

    const getAnimationClasses = () => {
        let classes = '';

        if (animated) {
            classes += 'transition-all duration-300 hover:scale-110 ';
        }

        if (loading) {
            classes += 'animate-pulse ';
        }

        return classes;
    };

    // Handle SVG file variants
    if (variant === 'svg' || variant === 'svg-large') {
        const svgSrc = variant === 'svg-large' ? LoopXLargeSvg : LoopXSvg;

        // For svg-large, use a wider aspect ratio to match the 1024x768 dimensions
        // For regular svg, maintain square aspect ratio but optimize for content
        const width = variant === 'svg-large' ? Math.round(sizeValue * 1.33) : sizeValue;
        const height = sizeValue;

        return (
            <img
                src={svgSrc}
                alt="LoopX Logo"
                width={width}
                height={height}
                className={`${className} ${getAnimationClasses()}`}
                style={{
                    filter: isDarkMode ? 'invert(1) brightness(1.2) contrast(1.1)' : 'none',
                    opacity: loading ? 0.7 : 1,
                    objectFit: 'contain',
                    objectPosition: 'center'
                }}
            />
        );
    }

    return (
        <svg
            width={sizeValue}
            height={sizeValue}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`${className} ${getAnimationClasses()}`}
            role="img"
            aria-label="LoopX Logo"
        >
            {(variant === 'primary' || loading) && renderGradients()}

            {variant !== 'icon-only' ? (
                <>
                    {/* Infinity loop design */}
                    <path
                        d="M8 12c0-3 2-5 4-5s4 2 4 5-2 5-4 5-4-2-4-5z"
                        fill="none"
                        stroke={getFillColor()}
                        strokeWidth="2"
                        opacity="0.6"
                    />
                    <path
                        d="M12 12c0-3 2-5 4-5s4 2 4 5-2 5-4 5-4-2-4-5z"
                        fill="none"
                        stroke={getFillColor()}
                        strokeWidth="2"
                        opacity="0.6"
                    />

                    {/* X intersection */}
                    <path
                        d="M8 8l8 8M16 8l-8 8"
                        stroke={getFillColor()}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        opacity="0.9"
                    />

                    {/* Center emphasis */}
                    <circle
                        cx="12"
                        cy="12"
                        r="1.5"
                        fill={getFillColor()}
                    >
                        {loading && (
                            <animate
                                attributeName="r"
                                values="1.5;2.5;1.5"
                                dur="1.5s"
                                repeatCount="indefinite"
                            />
                        )}
                    </circle>
                </>
            ) : (
                /* Icon-only variant - simplified X */
                <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke={getFillColor()}
                    strokeWidth="3"
                    strokeLinecap="round"
                >
                    {loading && (
                        <animateTransform
                            attributeName="transform"
                            type="rotate"
                            values="0 12 12;360 12 12"
                            dur="2s"
                            repeatCount="indefinite"
                        />
                    )}
                </path>
            )}
        </svg>
    );
};

export default Logo;